import { AppProvider } from '@shopify/polaris';
import ReactDOM from "react-dom/client";
import App from "./App.js";
import React from "react";
import "./index.css";
import {
    ApolloClient,
    ApolloLink,
    InMemoryCache,
    ApolloProvider,
    Observable,
    createHttpLink,
    split,
} from "@apollo/client";
import { setContext } from '@apollo/client/link/context/index.js';
import { onError } from '@apollo/client/link/error/index.js';
import { getMainDefinition } from '@apollo/client/utilities/index.js';
import {
    HOST_URL
} from "./ShopifyConstants.js";

const CHUNK_LOAD_RECOVERY_KEY = "cartifind_chunk_load_recovered_at";

const isDevelopment = process.env.NODE_ENV !== "production";
const debugLog = (...args) => {
    if (isDevelopment) console.log(...args);
};
const debugWarn = (...args) => {
    if (isDevelopment) console.warn(...args);
};
const markDebugPerformance = (name) => {
    if (isDevelopment && window.performance?.mark) {
        window.performance.mark(name);
    }
};
const measureDebugPerformance = (name, start, end) => {
    if (!isDevelopment || !window.performance?.measure || !window.performance?.getEntriesByName) {
        return null;
    }
    window.performance.measure(name, start, end);
    return window.performance.getEntriesByName(name)[0] || null;
};

const getShopifyEmbeddedIdToken = async () => {
    try {
        if (typeof window === "undefined" || typeof window.shopify?.idToken !== "function") {
            return null;
        }
        const idToken = await window.shopify.idToken();
        return typeof idToken === "string" && idToken.length > 0 ? idToken : null;
    } catch (error) {
        debugWarn("Shopify App Bridge id token unavailable:", error?.message || error);
        return null;
    }
};

const isChunkLoadFailure = (error) => {
    const message = String(error?.message || error?.reason?.message || error?.reason || "");
    const name = String(error?.name || error?.reason?.name || "");
    return name === "ChunkLoadError" ||
        message.includes("ChunkLoadError") ||
        message.includes("Loading chunk") ||
        message.includes("Loading CSS chunk");
};

const recoverFromChunkLoadFailure = (error) => {
    if (!isChunkLoadFailure(error)) {
        return;
    }
    const now = Date.now();
    const lastRecoveryAt = Number(sessionStorage.getItem(CHUNK_LOAD_RECOVERY_KEY) || 0);
    if (Number.isFinite(lastRecoveryAt) && now - lastRecoveryAt < 30000) {
        return;
    }
    sessionStorage.setItem(CHUNK_LOAD_RECOVERY_KEY, String(now));
    const nextURL = new URL(window.location.href);
    nextURL.searchParams.set("chunk_reload", String(now));
    window.location.replace(nextURL.toString());
};

window.addEventListener("error", (event) => {
    recoverFromChunkLoadFailure(event?.error || event?.message);
});

window.addEventListener("unhandledrejection", (event) => {
    recoverFromChunkLoadFailure(event);
});

// Note: Shopify App Bridge handles web vitals automatically via index.html script

// 🚀 FIX: Apollo Client initialization is SYNCHRONOUS - no API calls needed
// Creating an Apollo Client instance just sets up links and cache - no network requests
const initializeApolloClient = () => {
    // 📊 Performance measurement: Track Apollo initialization time
    markDebugPerformance('apollo-init-start');

    try {
        // 🚀 API ENDPOINT CONFIGURATION
        const apiEndpoint = `${HOST_URL}/api`;
        debugLog(`🔗 API Endpoint: ${apiEndpoint}`);
        debugLog(`🔗 HOST_URL: ${HOST_URL}`);

        // Validate API endpoint
        if (!HOST_URL || HOST_URL === 'undefined') {
            throw new Error('HOST_URL is not defined. Please check ShopifyConstants.js');
        }

        let httpLink = createHttpLink({
            uri: apiEndpoint,
            // Add timeout to prevent hanging requests
            timeout: 60000,
        });

        const wsProtocol = HOST_URL.startsWith('https') ? 'wss:' : 'ws:';
        const wsHost = HOST_URL.replace(/^https?:\/\//, '').split('/')[0];
        const wsUrl = `${wsProtocol}//${wsHost}/api`;

        debugLog(`🔗 HTTP GraphQL URL: ${apiEndpoint} (via CloudFront)`);

        const makeLazySubscriptionLink = () => {
            let wsLinkPromise = null;
            const getWsLink = () => {
                if (wsLinkPromise == null) {
                    debugLog(`🔌 Loading WebSocket subscription link on demand: ${wsUrl}`);
                    wsLinkPromise = Promise.all([
                        import('graphql-ws'),
                        import('@apollo/client/link/subscriptions/index.js'),
                    ]).then(([graphqlWsModule, subscriptionsModule]) => {
                        const { createClient } = graphqlWsModule;
                        const { GraphQLWsLink } = subscriptionsModule;
                        return new GraphQLWsLink(
                            createClient({
                                url: wsUrl,
                                connectionParams: async () => {
                                    const embeddedShopifyIdToken = await getShopifyEmbeddedIdToken();
                                    const latestToken = localStorage.getItem("token");
                                    const latestShopName = localStorage.getItem("shopName");
                                    const latestStorePlatform = localStorage.getItem("storePlatform");

                                    return {
                                        ...(embeddedShopifyIdToken ? { Authorization: `Bearer ${embeddedShopifyIdToken}` } : {}),
                                        token: (latestToken != null) && (latestToken.length > 0) ? latestToken : "",
                                        shop: latestShopName != null && latestShopName.length > 0 && latestToken != null && latestToken.length > 0 ? latestShopName : undefined,
                                        platform: (latestStorePlatform != null) && (latestStorePlatform.length > 0) ? latestStorePlatform : "",
                                    };
                                },
                                on: {
                                    connected: () => debugLog('🔌 WebSocket connected'),
                                    closed: () => debugLog('🔌 WebSocket closed'),
                                    error: (error) => console.error('🔌 WebSocket error:', error),
                                },
                            })
                        );
                    });
                }
                return wsLinkPromise;
            };

            return new ApolloLink((operation) => new Observable((observer) => {
                let subscription = null;
                let cancelled = false;
                getWsLink()
                    .then((wsLink) => {
                        if (cancelled) return;
                        subscription = wsLink.request(operation).subscribe(observer);
                    })
                    .catch((error) => {
                        if (!cancelled) observer.error(error);
                    });
                return () => {
                    cancelled = true;
                    subscription?.unsubscribe?.();
                };
            }));
        };

        // Split link: use WebSocket for subscriptions, HTTP for queries/mutations
        const splitLink = split(
            ({ query }) => {
                const definition = getMainDefinition(query);
                return (
                    definition.kind === 'OperationDefinition' &&
                    definition.operation === 'subscription'
                );
            },
            makeLazySubscriptionLink(),
            httpLink
        );

        // 🚀 ERROR HANDLING: Add error link for better diagnostics
        const isSilentTelemetryOperation = (operation) => {
            const eventName = operation?.variables?.eventName;
            return operation?.operationName === 'log' &&
                typeof eventName === 'string' &&
                eventName.startsWith('SHOPIFY_WEB_VITAL_');
        };

        const errorLink = onError(({ graphQLErrors, networkError, operation, forward }) => {
            const silentTelemetryOperation = isSilentTelemetryOperation(operation);
            debugLog('🔍 Error link triggered:', {
                hasGraphQLErrors: !!graphQLErrors,
                hasNetworkError: !!networkError,
                operationName: operation?.operationName,
                silentTelemetryOperation,
            });

            if (graphQLErrors && !silentTelemetryOperation) {
                console.error(`[GraphQL Errors]: ${graphQLErrors.length} error(s) found`);
                graphQLErrors.forEach(({ message, locations, path }) => {
                    console.error(
                        `[GraphQL error]: Message: ${message}, Location: ${locations}, Path: ${path}`
                    );
                });
            }

            if (networkError) {
                if (silentTelemetryOperation) {
                    debugLog('📊 Web Vitals telemetry failed without affecting merchant workflow:', {
                        operationName: operation?.operationName,
                        eventName: operation?.variables?.eventName,
                        message: networkError.message,
                    });
                    return;
                }
                console.error(`[Network error]:`, networkError);
                console.error(`API Endpoint: ${apiEndpoint}`);
                console.error(`Network Error Type: ${networkError.constructor.name}`);
                console.error(`Network Error Message: ${networkError.message}`);

                // Check for specific error types
                if (networkError.statusCode) {
                    console.error(`HTTP Status: ${networkError.statusCode}`);
                    if (networkError.statusCode === 502) {
                        console.error('⚠️ API Server Unavailable (502) - Backend might be down or unreachable');
                    } else if (networkError.statusCode === 503) {
                        console.error('⚠️ API Service Unavailable (503) - Server might be overloaded');
                    } else if (networkError.statusCode === 504) {
                        console.error('⚠️ API Gateway Timeout (504) - Request took too long');
                    }
                }

                // Log operation details for debugging
                if (operation) {
                    const failedOperation = {
                        operationName: operation.operationName,
                        variables: operation.variables,
                    };
                    console.error('Failed operation:', failedOperation);
                    console.error(`Failed operation details: ${JSON.stringify(failedOperation)}`);
                }
            }
        });

        const queryParams = new URLSearchParams(window.location.search);
        const tokenParam = queryParams.get("token");
        const shopNameParam = queryParams.get("shopName");
        const storePlatformParam = queryParams.get("storePlatform");

        if (tokenParam != null && tokenParam.length > 0 && shopNameParam != null && shopNameParam.length > 0 && storePlatformParam != null && storePlatformParam.length > 0) {
            // Clear any existing auth data to prevent conflicts
            localStorage.removeItem("token");
            localStorage.removeItem("shopName");
            localStorage.removeItem("storePlatform");

            // Set new mobile auth parameters
            localStorage.setItem("token", tokenParam);
            localStorage.setItem("shopName", shopNameParam);
            localStorage.setItem("storePlatform", storePlatformParam);

            debugLog('📱 Mobile auth parameters set:', {
                tokenLength: tokenParam.length,
                shopName: shopNameParam,
                platform: storePlatformParam
            });
        }

        const authLink = setContext(async (_, { headers }) => {
            const embeddedShopifyIdToken = await getShopifyEmbeddedIdToken();
            const latestToken = localStorage.getItem("token");
            const latestShopName = localStorage.getItem("shopName");
            const latestStorePlatform = localStorage.getItem("storePlatform");
            const authHeaders = {
                ...headers,
                ...(embeddedShopifyIdToken ? { Authorization: `Bearer ${embeddedShopifyIdToken}` } : {}),
                token: (latestToken != null) && (latestToken.length > 0) ? latestToken : "",
                shop:
                    latestShopName != null &&
                        latestShopName.length > 0 &&
                        latestToken != null &&
                        latestToken.length > 0
                        ? latestShopName
                        : undefined,
                platform: (latestStorePlatform != null) && (latestStorePlatform.length > 0) ? latestStorePlatform : "",
            };

            // 🚀 DEBUG: Log auth headers (without sensitive data) - only log once per request to avoid spam
            if (!authLink._loggedOnce) {
                debugLog('🔐 [AUTH DEBUG] Auth headers prepared:', {
                    hasShopifyIdToken: !!embeddedShopifyIdToken,
                    hasToken: !!(authHeaders.token && authHeaders.token.length > 0),
                    hasShop: !!authHeaders.shop,
                    hasPlatform: !!(authHeaders.platform && authHeaders.platform.length > 0),
                    shopName: latestShopName ? latestShopName.substring(0, 20) + '...' : 'none',
                    tokenLength: authHeaders.token ? authHeaders.token.length : 0
                });
                authLink._loggedOnce = true;
            }

            return { headers: authHeaders };
        });

        // Optimized cache configuration with better type policies
        const cache = new InMemoryCache({
            typePolicies: {
                Query: {
                    fields: {
                        getOrderList: {
                            keyArgs: false,
                            merge(existing, incoming) {
                                // A null result is cached as null. The default parameter does not
                                // replace null, and null.map throws, which can leave this query unsettled.
                                const previous = Array.isArray(existing) ? existing : [];
                                if (!Array.isArray(incoming)) {
                                    return previous;
                                }
                                const shopifyOrderSet = new Set(
                                    previous.map((order) => order && order.orderNumber)
                                );
                                const uniqueIncoming = [];
                                for (let i = 0; i < incoming.length; i++) {
                                    const order = incoming[i];
                                    if (order != null && !shopifyOrderSet.has(order.orderNumber)) {
                                        uniqueIncoming.push(order);
                                        shopifyOrderSet.add(order.orderNumber);
                                    }
                                }
                                if (uniqueIncoming.length === 0) {
                                    return previous;
                                }
                                return [...previous, ...uniqueIncoming];
                            },
                        },
                        // Cache authentication responses for faster redirects
                        saveShopifyToken: {
                            keyArgs: false,
                            merge: true,
                        },
                    },
                },
            },
        });

        // 🚀 PERFORMANCE OPTIMIZATION: Disable cache persistence for faster initial load
        // Cache persistence adds 200-400ms to initial load with minimal benefit
        // Apollo's in-memory cache is sufficient for session-based data
        // If you need persistence, uncomment below and use localStorage (faster than sessionStorage)
        /*
        persistCache({
            cache,
            storage: window.localStorage, // localStorage is faster than sessionStorage
            maxSize: 102400, // 100KB limit - keep it small
            debug: false,
        }).then(() => {
            console.log('✅ Apollo cache persistence initialized');
        }).catch((error) => {
            console.warn('⚠️ Apollo cache persistence failed (non-critical):', error);
        });
        */

        const client = new ApolloClient({
            link: errorLink.concat(authLink).concat(splitLink),
            cache,
            defaultOptions: {
                watchQuery: {
                    errorPolicy: 'all',
                    fetchPolicy: 'cache-and-network', // Show cache first, then update with network
                    notifyOnNetworkStatusChange: true,
                },
                query: {
                    errorPolicy: 'all',
                    fetchPolicy: 'cache-and-network', // Show cache first, then update with network
                    notifyOnNetworkStatusChange: true, // 🚀 FIX: Ensure all queries report network status changes
                },
            },
        });

        // 🚀 DEBUG: Test Apollo Client connection
        debugLog('🔍 [APOLLO DEBUG] Apollo Client created:', {
            uri: apiEndpoint,
            hasCache: !!client.cache,
            hasLink: !!client.link,
            linkType: client.link?.constructor?.name,
            cacheType: client.cache?.constructor?.name,
            defaultOptions: client.defaultOptions
        });

        // Test query to verify Apollo Client is working
        // This will help us identify if the issue is with Apollo Client setup
        try {
            // Just log that we're ready - don't execute a test query as it might fail
            debugLog('✅ [APOLLO DEBUG] Apollo Client is ready to execute queries');
            debugLog('✅ [APOLLO DEBUG] Client methods available:', {
                hasQuery: typeof client.query === 'function',
                hasMutate: typeof client.mutate === 'function',
                hasReadQuery: typeof client.readQuery === 'function'
            });
        } catch (testError) {
            console.error('❌ [APOLLO DEBUG] Apollo Client test failed:', testError);
        }

        // 📊 Performance measurement: Log Apollo initialization time
        markDebugPerformance('apollo-init-end');
        const apolloInitTime = measureDebugPerformance('apollo-init-duration', 'apollo-init-start', 'apollo-init-end');
        if (apolloInitTime) {
            debugLog(`⚡ Apollo Client initialized in ${apolloInitTime.duration.toFixed(0)}ms`);
        }

        return client;
    } catch (error) {
        console.error('Apollo Client initialization failed:', error);
        throw error;
    }
};

// 🚀 FIX: Initialize Apollo Client immediately - it's synchronous, no waiting needed.
// App Bridge readiness is handled inside App and must not block React first paint.
const ApolloClientWrapper = ({ children }) => {
    // Initialize Apollo Client immediately on mount - it's just configuration, no API calls
    const [client] = React.useState(() => {
        try {
            debugLog('🚀 [APOLLO DEBUG] Initializing Apollo Client synchronously on mount...');
            return initializeApolloClient();
        } catch (err) {
            console.error('❌ Apollo Client initialization failed:', err);
            return null;
        }
    });
    const [error] = React.useState(null);

    // Apollo Client is already initialized synchronously above
    // No need to wait - it's just configuration, not an API call

    // 🚀 DEBUG: Verify Apollo Client is accessible from context (must be before any early returns)
    React.useEffect(() => {
        if (client) {
            debugLog('✅ [APOLLO DEBUG] Apollo Client is now available in React context');
            debugLog('✅ [APOLLO DEBUG] Components can now use useQuery, useMutation, etc.');
        }
    }, [client]);

    // Show error state if Apollo fails to initialize
    if (error) {
        console.error('❌ Apollo Client Error Details:', {
            message: error.message,
            stack: error.stack,
            name: error.name
        });
        return (
            <div style={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                height: '100vh',
                backgroundColor: '#fafafa',
                color: '#333',
                fontFamily: 'system-ui, -apple-system, sans-serif',
                padding: '20px'
            }}>
                <h2 style={{ color: '#d72c0d', marginBottom: '10px' }}>API Connection Failed</h2>
                <p style={{ marginBottom: '20px', textAlign: 'center', maxWidth: '500px' }}>
                    Unable to connect to the API server. This might be due to network issues or the server being unavailable.
                </p>
                <div style={{
                    backgroundColor: '#f6f6f7',
                    padding: '15px',
                    borderRadius: '4px',
                    marginBottom: '20px',
                    maxWidth: '600px',
                    fontSize: '12px',
                    fontFamily: 'monospace',
                    overflow: 'auto',
                    maxHeight: '200px'
                }}>
                    <strong>Error:</strong> {error.message || 'Unknown error'}
                </div>
                <button
                    onClick={() => window.location.reload()}
                    style={{
                        padding: '10px 20px',
                        backgroundColor: '#008060',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontSize: '14px'
                    }}
                >
                    Refresh Page
                </button>
            </div>
        );
    }

    // Show loading state only if client failed to initialize
    if (!client) {
        return (
            <div style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: '100vh',
                backgroundColor: '#f6f6f7'
            }}>
                <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: '10px'
                }}>
                    <div style={{
                        border: '2px solid #e1e3e5',
                        borderTop: '2px solid #008060',
                        borderRadius: '50%',
                        width: '24px',
                        height: '24px',
                        animation: 'spin 1s linear infinite'
                    }} />
                    <div style={{
                        color: '#6d7175',
                        fontSize: '14px'
                    }}>
                        {'Initializing API...'}
                    </div>
                </div>
            </div>
        );
    }

    // Apollo is ready, render the app
    debugLog('✅ [APOLLO DEBUG] ApolloProvider rendering with client:', {
        hasClient: !!client,
        clientType: client?.constructor?.name,
        cacheType: client?.cache?.constructor?.name,
        hasLink: !!client?.link,
        linkChain: client?.link ? 'errorLink -> authLink -> httpLink' : 'none'
    });

    return (
        <ApolloProvider client={client}>
            {children}
        </ApolloProvider>
    );
};

// Optimized app initialization - now renders immediately
const initializeApp = () => {
    // 📊 Performance measurement: Track total app initialization time
    markDebugPerformance('app-init-start');

    // 🚀 FIX: Ensure root element exists before creating React root
    const rootElement = document.getElementById("root");
    if (!rootElement) {
        console.error('❌ Root element not found! Cannot initialize React app.');
        // Show error message in body
        document.body.innerHTML = `
            <div style="display: flex; justify-content: center; align-items: center; height: 100vh; flex-direction: column; gap: 20px; font-family: system-ui, sans-serif;">
                <h2 style="color: #d72c0d;">Application Error</h2>
                <p>Root element not found. Please refresh the page.</p>
                <button onclick="window.location.reload()" style="padding: 10px 20px; background: #008060; color: white; border: none; border-radius: 4px; cursor: pointer;">
                    Refresh Page
                </button>
            </div>
        `;
        return;
    }

    debugLog('✅ Root element found, creating React root...');
    const root = ReactDOM.createRoot(rootElement);

    // Enhanced React component with loader management
    const AppWithLoader = () => {
        React.useEffect(() => {
            // 📊 Performance measurement: Log total app initialization time
            markDebugPerformance('app-init-end');
            const appInitTime = measureDebugPerformance('app-init-duration', 'app-init-start', 'app-init-end');
            if (appInitTime) {
                debugLog(`🚀 App rendered in ${appInitTime.duration.toFixed(0)}ms`);
            }

            // 🚀 CLS FIX: Add react-ready class first, then hide loader
            // This ensures smooth transition without layout shifts
            const rootEl = document.getElementById('root');
            if (rootEl) {
                rootEl.classList.add('react-ready');
            }

            // Hide the HTML loader when React is ready
            // hideInitialLoader now handles app container visibility synchronization
            if (window.hideInitialLoader) {
                // Use requestAnimationFrame to ensure DOM is ready
                requestAnimationFrame(() => {
                    window.hideInitialLoader();
                });
            } else {
                // Fallback: if hideInitialLoader doesn't exist, hide loader directly
                debugWarn('⚠️ hideInitialLoader not found, hiding loader directly');
                const loader = document.querySelector('.loader-container');
                if (loader) {
                    loader.classList.add('hidden');
                }
            }

            // Safety timeout: Force hide loader after 5 seconds if still visible
            setTimeout(() => {
                const loader = document.querySelector('.loader-container');
                if (loader && !loader.classList.contains('hidden')) {
                    debugWarn('⚠️ Loader still visible after 5s, forcing hide');
                    loader.classList.add('hidden');
                }
            }, 5000);

            // 🚀 FIX: Ensure app-container is visible even if hideInitialLoader fails
            setTimeout(() => {
                const appContainer = document.querySelector('.app-container');
                if (appContainer && !appContainer.classList.contains('loaded')) {
                    debugWarn('⚠️ App container not visible, forcing visibility');
                    appContainer.classList.add('loaded');
                }
            }, 1000);

            // Note: Shopify App Bridge handles web vitals automatically via index.html script
        }, []);

        return (
            <div className="app-container">
                <App />
            </div>
        );
    };

    // 🚀 PARALLEL INITIALIZATION: React renders immediately while Apollo initializes
    // This saves ~500ms by not blocking the render on Apollo Client setup
    try {
        root.render(
            <React.StrictMode>
                <ApolloClientWrapper>
                    <AppProvider>
                        <AppWithLoader />
                    </AppProvider>
                </ApolloClientWrapper>
            </React.StrictMode>
        );
        debugLog('✅ React app rendered successfully');
    } catch (error) {
        console.error('❌ Failed to render React app:', error);
        // Show error message
        rootElement.innerHTML = `
            <div style="display: flex; justify-content: center; align-items: center; height: 100vh; flex-direction: column; gap: 20px; font-family: system-ui, sans-serif;">
                <h2 style="color: #d72c0d;">Application Error</h2>
                <p>Failed to initialize React application.</p>
                <pre style="background: #f6f6f7; padding: 10px; border-radius: 4px; max-width: 600px; overflow: auto;">${error.message}</pre>
                <button onclick="window.location.reload()" style="padding: 10px 20px; background: #008060; color: white; border: none; border-radius: 4px; cursor: pointer;">
                    Refresh Page
                </button>
            </div>
        `;
    }
};

// App Bridge readiness is useful diagnostics, but it must not block React first paint.
function reportAppBridgeReadiness(maxWait = 10000) {
    const startTime = Date.now();

    if (window.appBridgeReady) {
        return;
    }

    function onAppBridgeReady() {
        window.removeEventListener('appBridgeReady', onAppBridgeReady);
        debugLog('✅ Shopify App Bridge ready after React initialization');
    }
    window.addEventListener('appBridgeReady', onAppBridgeReady);

    function checkAppBridge() {
        if (window.appBridgeReady || window.shopify || window.Shopify || (window.AppBridge && window.AppBridge.default)) {
            window.removeEventListener('appBridgeReady', onAppBridgeReady);
            debugLog('✅ Shopify App Bridge detected after React initialization');
        } else if (Date.now() - startTime < maxWait) {
            setTimeout(checkAppBridge, 100);
        } else {
            debugWarn('⚠️ Shopify App Bridge not loaded after', maxWait, 'ms. React shell already initialized; App will use fallback auth handling.');
            window.removeEventListener('appBridgeReady', onAppBridgeReady);
        }
    }

    setTimeout(checkAppBridge, 200);
}

// Initialize app when DOM is ready. App Bridge readiness is handled inside App without blocking first paint.
function startApp() {
    // 🚀 FIX: Ensure root element exists before starting
    const rootElement = document.getElementById("root");
    if (!rootElement) {
        console.error('❌ Root element not found in DOM. Waiting for DOM to be ready...');
        // Wait for DOM to be ready
        if (document.readyState === 'loading') {
            document.addEventListener('DOMContentLoaded', () => {
                const root = document.getElementById("root");
                if (!root) {
                    console.error('❌ Root element still not found after DOMContentLoaded');
                    document.body.innerHTML = `
                        <div style="display: flex; justify-content: center; align-items: center; height: 100vh; flex-direction: column; gap: 20px; font-family: system-ui, sans-serif;">
                            <h2 style="color: #d72c0d;">Application Error</h2>
                            <p>Root element (#root) not found in HTML. Please check index.html.</p>
                            <button onclick="window.location.reload()" style="padding: 10px 20px; background: #008060; color: white; border: none; border-radius: 4px; cursor: pointer;">
                                Refresh Page
                            </button>
                        </div>
                    `;
                    return;
                }
                initializeApp();
                reportAppBridgeReadiness();
            });
        } else {
            // DOM is ready but root not found - this is a critical error
            console.error('❌ Root element not found and DOM is already ready');
            document.body.innerHTML = `
                <div style="display: flex; justify-content: center; align-items: center; height: 100vh; flex-direction: column; gap: 20px; font-family: system-ui, sans-serif;">
                    <h2 style="color: #d72c0d;">Application Error</h2>
                    <p>Root element (#root) not found in HTML. Please check index.html.</p>
                    <button onclick="window.location.reload()" style="padding: 10px 20px; background: #008060; color: white; border: none; border-radius: 4px; cursor: pointer;">
                        Refresh Page
                    </button>
                </div>
            `;
        }
        return;
    }

    debugLog('✅ Root element found, starting app initialization...');
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', () => {
            initializeApp();
            reportAppBridgeReadiness();
        });
    } else {
        initializeApp();
        reportAppBridgeReadiness();
    }
}

startApp();
